import { Flex, Box } from '@chakra-ui/react';
import { useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getEditInstrumentData,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  getDashboardReportingPeriod,
  createInstrument,
  setEditInstrumentOutput,
  updateInstrument,
  setEditInstrumentData,
  setAllInstruments,
  setAllInstrumentOutputs,
  getTimeFilter,
  getAllInstruments,
  getAllInstrumentOutputs,
} from 'redux/DashboardSliceV2';
import { AppDispatch, getRandomColor } from 'utils/GlobalHelpers';

import PriviewFilterByComponent from '../PreviewFilterBy';

import Variance from './Variance';

export interface WrapperProperties {
  columnPositions?: any;
  columnSettings: any;
  output: any;
}

const VarianceWrapper = ({ columnSettings, output, columnPositions }: WrapperProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const allInstruments = useSelector(getAllInstruments);
  const allInstrumentsOutputs = useSelector(getAllInstrumentOutputs);
  const timeFilter = useSelector(getTimeFilter);
  const dashboardReportingPeriod = useSelector(getDashboardReportingPeriod);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const parentReference = useRef() as React.MutableRefObject<HTMLDivElement>;
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const editInstrumentData = useSelector(getEditInstrumentData);

  const handleApplyFilter = (filterAppliedData: any, filterItem: any) => {
    const editData = JSON.parse(JSON.stringify(output));

    if (selectedDashboardScenario && filterAppliedData?.length) {
      // Update all indicators that match the dimension_id
      const updatedIndicators = editData?.instrument_indicators?.map((indicator: any) => {
        if (indicator?.dimension_id === filterItem[0]?.dimensionId) {
          return {
            ...indicator,
            formatting: {
              ...indicator.formatting,
              dimensionItems: filterAppliedData
                .filter((item: any) => item.status)
                .map((item: any) => {
                  return {
                    dimension_item_id: item.dimension_item_id,
                    dimension_item_name: item.name,
                    color: getRandomColor(),
                  };
                }),
            },
          };
        }
        return indicator;
      });

      const reportingPeriod = dashboardReportingPeriod
        ? {
            preset: dashboardReportingPeriod.preset || 'Custom',
            start: dashboardReportingPeriod.start || '',
            end: dashboardReportingPeriod.end || '',
          }
        : null;
      const compareScenarioId = compareDashboardScenario?.id || null;

      if (editInstrumentData?.instrument_type) {
        if (!editData?.id) {
          const newData = JSON.parse(JSON.stringify(editData));
          newData.instrument_indicators = updatedIndicators;
          dispatch(
            createInstrument({
              request_body: {
                ...newData,
                scenario_id: selectedDashboardScenario?.id
                  ? `${selectedDashboardScenario?.id}`
                  : '',
                save: false,
                data_values: true,
                start_date: output?.start_date || '',
                end_date: output?.end_date || '',
                reporting_period: reportingPeriod,
                compare_scenario: compareScenarioId,
              },
            }),
          ).then((result: any) => {
            dispatch(setEditInstrumentOutput(result?.payload?.data));
            dispatch(
              setEditInstrumentData({
                ...output,
                instrument_indicators: result?.payload?.instrument_indicators,
              }),
            );
          });
        } else {
          const newData = JSON.parse(JSON.stringify(editData));
          newData.instrument_indicators = updatedIndicators;
          dispatch(
            updateInstrument({
              request_body: {
                ...newData,
                scenario_id: selectedDashboardScenario?.id
                  ? `${selectedDashboardScenario?.id}`
                  : '',
                save: false,
                data_values: true,
                start_date: timeFilter?.start || '',
                end_date: timeFilter?.end || '',
                reporting_period: reportingPeriod,
                compare_scenario: compareScenarioId,
              },
            }),
          ).then((result: any) => {
            dispatch(setEditInstrumentOutput(result?.payload?.data));
            dispatch(
              setEditInstrumentData({
                ...output,
                instrument_indicators: result?.payload?.instrument_indicators,
              }),
            );
          });
        }
      } else {
        const foundIndex = allInstruments.findIndex(
          (instrument: any) => instrument.id === output.id,
        );
        if (foundIndex > -1) {
          const list = JSON.parse(JSON.stringify(allInstruments));
          list[foundIndex].instrument_indicators = updatedIndicators;
          dispatch(setAllInstruments([...list]));
          dispatch(
            updateInstrument({
              request_body: {
                ...list[foundIndex],
                save: true,
                data_values: true,
                start_date: timeFilter?.start || '',
                end_date: timeFilter?.end || '',
                reporting_period: reportingPeriod,
                compare_scenario: compareScenarioId,
                scenario_id: selectedDashboardScenario?.id
                  ? `${selectedDashboardScenario?.id}`
                  : '',
              },
            }),
          ).then((result) => {
            dispatch(
              setAllInstrumentOutputs({
                ...allInstrumentsOutputs,
                [result.payload.id]: { ...result?.payload?.data },
              }),
            );
          });
        }
      }
    }
  };

  const isDimensionFilter = useMemo(() => {
    const indicatorsWithDimensions = output?.instrument_indicators?.filter((indicator: any) => {
      return (
        indicator?.formatting &&
        Array.isArray(indicator?.formatting?.dimensionItems) &&
        indicator?.formatting?.dimensionItems?.length > 1 &&
        indicator?.dimension_id !== null &&
        indicator?.dimension_id !== ''
      );
    });

    if (!indicatorsWithDimensions?.length) return false;

    const firstDimensionId: any = indicatorsWithDimensions[0]?.dimension_id;
    const allSameDimension = indicatorsWithDimensions.every(
      (indicator: any) => indicator?.dimension_id === firstDimensionId,
    );

    const actualIndicators = output?.instrument_indicators.filter(
      (indicator: any) => indicator.indicator_id,
    );

    const eachIndicatorHasSameDimension = actualIndicators.every(
      (indicator: any) => indicator?.dimension_id === firstDimensionId,
    );
    return (allSameDimension && eachIndicatorHasSameDimension) || false;
  }, [output?.instrument_indicators?.length, output?.instrument_indicators]);

  // height='100%' lets the inner Box propagate a constrained height to Variance,
  // which Variance needs for overflowY to actually scroll and for sticky thead
  // to have something to stick to.
  return (
    <Flex borderRadius='15px' flexDirection='column' gap={1} height='100%' ref={parentReference}>
      {isDimensionFilter && (
        <Flex
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          flexShrink={0}
          m={1}
        >
          <PriviewFilterByComponent widget={output} handleApplyFilter={handleApplyFilter} />
        </Flex>
      )}
      {/* flex={1} minH={0} fills remaining height so Variance's h='100%' resolves
          to a real pixel value and creates a scrolling container. */}
      <Box px={4} flex={1} minH={0}>
        <Variance
          output={output}
          columnSettings={columnSettings}
          columnPositions={columnPositions}
        />
      </Box>
    </Flex>
  );
};

export default VarianceWrapper;
